import { Icon } from "@iconify/react";
import { useCallback, useEffect, useState } from "react";
import { Loader } from "@/components/motion/loader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/locales/i18n-provider";
import {
  attachmentTypeLabel,
  formatFileSize,
  isImageAttachment,
  isPreviewableAttachment,
  saveBlobAs,
  type TicketAttachmentView,
} from "../utils/ticket-attachments";

const maxTextPreviewBytes = 200 * 1024;

/**
 * 附件预览。本地文件直接读，服务端附件先取回 Blob——附件端点要带 Authorization，
 * `<img src>` 直链拿不到。两种来源在这里收敛成同一个 Blob，后面的渲染就只有一套。
 */
export function AttachmentPreviewDialog({
  attachment,
  loadContent,
  onClose,
}: {
  attachment: TicketAttachmentView | null;
  /** 取服务端附件的字节；**必须是稳定引用**（useCallback），否则每次渲染都会重新拉一遍。 */
  loadContent?: (attachment: TicketAttachmentView) => Promise<Blob>;
  onClose: () => void;
}) {
  const { intlLocale, t } = useI18n();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [textPreview, setTextPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchBlob = useCallback(
    (target: TicketAttachmentView) => (target.file ? Promise.resolve<Blob>(target.file) : (loadContent?.(target) ?? Promise.reject(new Error("no source")))),
    [loadContent],
  );

  useEffect(() => {
    setImageUrl(null);
    setTextPreview(null);
    setError(null);
    setBlob(null);
    setIsLoading(false);
    // 不能就地预览的（zip、Office、音视频）不预取：为了点亮一个下载按钮
    // 先默默拉回一个几十兆的文件，用户什么也没要求。改由下载按钮点击时再取。
    if (!attachment || !isPreviewableAttachment(attachment)) return;

    let cancelled = false;
    let objectUrl: string | null = null;
    setIsLoading(true);

    void fetchBlob(attachment)
      .then(async (resolved) => {
        if (cancelled) return;
        setBlob(resolved);

        if (isImageAttachment(attachment)) {
          objectUrl = URL.createObjectURL(resolved);
          setImageUrl(objectUrl);
          return;
        }

        const text = await resolved.slice(0, maxTextPreviewBytes).text();
        if (cancelled) return;
        setTextPreview(resolved.size > maxTextPreviewBytes ? `${text}\n\n${t("tickets.attachment.previewTruncated")}` : text);
      })
      .catch(() => {
        if (!cancelled) setError(t("tickets.attachment.previewError"));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, fetchBlob, t]);

  const download = async () => {
    if (!attachment) return;
    setIsDownloading(true);
    try {
      saveBlobAs(blob ?? (await fetchBlob(attachment)), attachment.name);
    } catch {
      setError(t("tickets.attachment.downloadError"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={attachment !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="pr-8 break-all">{attachment?.name ?? t("tickets.attachment.previewTitle")}</DialogTitle>
          <DialogDescription>
            {attachment ? `${attachmentTypeLabel(attachment, t)} · ${formatFileSize(attachment.size, intlLocale)}` : t("tickets.attachment.previewTitle")}
          </DialogDescription>
        </DialogHeader>

        {imageUrl && attachment ? (
          <div className="flex max-h-96 items-center justify-center overflow-auto rounded-lg bg-muted p-3">
            <img alt={attachment.name} className="max-h-80 max-w-full object-contain" src={imageUrl} />
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader className="text-current" label={t("tickets.attachment.reading")} size={16} />
            {t("tickets.attachment.reading")}
          </div>
        ) : null}

        {error ? <p className="min-h-24 py-6 text-center text-sm text-muted-foreground">{error}</p> : null}

        {textPreview !== null ? (
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-muted p-3 font-mono text-xs leading-5 text-foreground">
            {textPreview || t("tickets.attachment.emptyFile")}
          </pre>
        ) : null}

        {attachment && !isPreviewableAttachment(attachment) && !isLoading && !error ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("tickets.attachment.previewUnsupported")}</p>
        ) : null}

        {attachment ? (
          <div className="flex justify-end">
            <Button disabled={isDownloading} type="button" variant="outline" onClick={() => void download()}>
              {isDownloading ? <Loader className="text-current" label={t("tickets.attachment.downloading")} size={16} /> : <Icon icon="tabler:download" />}
              {t("tickets.actions.download")}
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
